import { useState, useEffect, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { DataGrid, GridNoRowsOverlay } from "@mui/x-data-grid";
import ExportCSV from "../ExportCsv/ExportCSV";

export const BaseTable = ({
  rows,
  columns,
  onFilterModelChange,
  rowOrCellClickRedirect,
  pagination = true,
  pageSize = 5,
  height,
  sx,
  rowsPerPageOptions = [5, 10, 20, 30],
  showExportCsvBtn,
  csvMorphemes,
  csvColumns,
  sortModel,
  onSortModelChange,
  isReadOnly,
  disableColumnResize = false,
  disableColumnFilter = false,
  ...restProps
}) => {
  const [pagesize, setPageSize] = useState(pageSize);

  useEffect(() => {
    setPageSize(pageSize);
  }, [pageSize]);

  const tableColumns = useMemo(() =>
    !isReadOnly
      ? columns
      : columns.filter(({ field }) => field.toLowerCase() !== "actions"),
    [isReadOnly, columns]
  );
  //TO SPECIFICALLY NOT SHOW COLUMNS; STATUS AND NOTES FOR TRANSCATIONS TABLE
  // const [updatedColumnsForTransactionsTable] = useState(()=>csvMorphemes==="transactions"?tableColumns.filter(({field})=>field!=="status" && field!=="notes"):[])
  const rowClickHandler = (data, event) => {
    event.stopPropagation();
    const correspondingColumnName = data.field;
    if (correspondingColumnName) {
      if (correspondingColumnName.toLowerCase() === "actions") return;
      else {
        if (rowOrCellClickRedirect) rowOrCellClickRedirect(data);
        return;
      }
    }
  };

  return (
    <>
      {showExportCsvBtn && rows && rows.length > 0 && (
        <ExportCSV
          rows={rows}
          columns={csvColumns}
          csvMorphemes={csvMorphemes}
        />
      )}
      <Stack alignItems="flex-end" spacing={2}>
        <Box sx={{ 
          height: "auto", 
          width: "100%",
          minHeight: "400px",
          maxWidth: "100%",
          overflow: "visible",
          paddingBottom: "16px",
          '& .MuiDataGrid-root': {
            border: 'none',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0,0,0,0.05)',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
          }
        }}>
          <DataGrid
            rows={rows}
            columns={tableColumns}
            {...(pagination ? { pageSize: pagesize, rowsPerPageOptions: rowsPerPageOptions } : { pageSize: rows?.length > 0 ? rows.length : 100 })}
            {...(!pagination && { hideFooter: true })}
            {...(sortModel && { sortModel: sortModel })}
            {...(onSortModelChange && { onSortModelChange: onSortModelChange })}
            onFilterModelChange={(model) => {
              if (onFilterModelChange) {
                onFilterModelChange(model);
              }
            }}
            disableSelectionOnClick
            pagination={true}
            disableColumnResize={true}
            disableColumnFilter={disableColumnFilter}
            components={GridNoRowsOverlay}
            autoHeight={true}
            disableVirtualization={true}
            sx={{
              ".MuiDataGrid-main": {
                overflowX: "auto !important",
                overflowY: "visible !important",
                height: "auto !important",
                maxHeight: "none !important",
              },
              ".MuiDataGrid-virtualScroller": {
                overflowX: "auto !important",
                overflowY: "visible !important",
                height: "auto !important",
                minHeight: "auto !important",
                maxHeight: "none !important",
                position: "relative !important",
              },
              ".MuiDataGrid-virtualScroller::-webkit-scrollbar:horizontal": {
                display: "block",
                height: "8px",
              },
              ".MuiDataGrid-virtualScroller::-webkit-scrollbar:vertical": {
                display: "none",
                width: "0",
              },
              ".MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              ".MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              ".MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
              ".MuiDataGrid-virtualScrollerContent": {
                overflowX: "visible",
                overflowY: "visible !important",
                paddingBottom: "24px !important",
                position: "relative !important",
              },
              ".MuiDataGrid-row:last-child": {
                marginBottom: "24px !important",
              },
              ".MuiDataGrid-root": {
                overflowX: "auto !important",
                overflowY: "visible !important",
              },
              ".MuiDataGrid-scrollbar--vertical": {
                display: "none !important",
                visibility: "hidden !important",
                opacity: "0 !important",
                width: "0 !important",
              },
              ".MuiDataGrid-scrollbar": {
                "&.MuiDataGrid-scrollbar--vertical": {
                  display: "none !important",
                  visibility: "hidden !important",
                },
              },
              ".MuiDataGrid-scrollbar--horizontal": {
                display: "block !important",
                visibility: "visible !important",
                opacity: "1 !important",
              },
              ".MuiDataGrid-columnHeaders": {
                backgroundColor: "rgba(0,0,0,0.02)",
                borderBottom: "2px solid rgba(0,0,0,0.1)",
              },
              ".MuiDataGrid-columnHeader": {
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "rgba(0,0,0,0.8)",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              },
              ".MuiDataGrid-cell": {
                whiteSpace: "normal !important",
                wordWrap: "break-word !important",
                wordBreak: "break-word !important",
                overflowWrap: "break-word !important",
                lineHeight: "1.4 !important",
                padding: "14px 16px !important",
                minHeight: "auto !important",
                maxHeight: "none !important",
                overflow: "visible !important",
                borderBottom: "1px solid rgba(0,0,0,0.05) !important",
                fontSize: "0.875rem",
                color: "rgba(0,0,0,0.87)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04) !important",
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "rgba(0,0,0,0.01)",
                },
              },
              ".MuiDataGrid-cellContent": {
                whiteSpace: "normal !important",
                wordWrap: "break-word !important",
                wordBreak: "break-word !important",
                overflowWrap: "break-word !important",
                lineHeight: "1.2 !important",
                overflow: "visible !important",
              },
              ".MuiDataGrid-row": {
                minHeight: "auto !important",
                maxHeight: "none !important",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04) !important",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(25, 118, 210, 0.08) !important",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.12) !important",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "rgba(0,0,0,0.01)",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.04) !important",
                  },
                },
              },
              ".MuiDataGrid-columnHeader": {
                whiteSpace: "normal !important",
                wordWrap: "break-word !important",
                lineHeight: "1.2 !important",
                backgroundColor: "#f8f9fa !important",
                color: "#495057 !important",
                fontWeight: "600 !important",
                borderBottom: "2px solid #e9ecef !important",
                padding: "12px 16px !important",
                "&:hover": {
                  backgroundColor: "#e9ecef !important",
                },
              },
              ".MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal !important",
                wordWrap: "break-word !important",
                lineHeight: "1.2 !important",
                overflow: "visible !important",
                color: "#495057 !important",
                fontWeight: "600 !important",
                fontSize: "0.875rem !important",
              },
              ".MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e9ecef !important",
                backgroundColor: "#f8f9fa !important",
                minHeight: "52px !important",
                position: "relative !important",
                marginTop: "24px !important",
                paddingTop: "8px !important",
                zIndex: 1,
              },
              ".MuiDataGrid-rowsContainer": {
                paddingBottom: "24px !important",
                marginBottom: "24px !important",
              },
              ".MuiTablePagination-root": {
                color: "#495057 !important",
              },
              ...sx,
            }}
            {...restProps}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              if (restProps.onPageSizeChange) {
                restProps.onPageSizeChange(newPageSize);
              }
            }}
            onCellClick={rowClickHandler}
          />
        </Box>
      </Stack>
    </>
  );
};
